import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import mermaid from 'mermaid';

// Base container for the Mermaid block
const MermaidBlockContainer = styled.div`
  position: relative;
  margin: 16px 0;
  padding: 0;
  border-radius: 6px;
  background-color: ${props => props.isActive ? 'var(--color-selection-bg, rgba(14, 101, 235, 0.05))' : 'transparent'};
  border: 1px solid ${props => props.isActive ? 'var(--color-selection-border, #dde5f2)' : '#e0e0e0'};
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.isActive ? 'var(--color-selection-bg, rgba(14, 101, 235, 0.05))' : 'var(--color-hover-bg, rgba(0, 0, 0, 0.02))'};
    border-color: ${props => props.isActive ? 'var(--color-selection-border, #dde5f2)' : '#c0c0c0'};
  }

  ${props => props.customStyles}
`;

// Mermaid editor container
const MermaidEditorContainer = styled.div`
  width: 100%;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  white-space: pre-wrap;
  padding: 12px;
  outline: none;
`;

// Mermaid view container
const MermaidViewContainer = styled.div`
  width: 100%;
  cursor: pointer;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  .mermaid-container {
    text-align: center;
    width: 100%;
    margin: 0 auto;
  }
  
  .mermaid-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100px;
    width: 100%;
    color: #6c757d;
    font-style: italic;
    background-color: #f8f9fa;
    border-radius: 4px;
  }
  
  .diagram-error {
    color: #dc3545;
    padding: 10px;
    border: 1px dashed #dc3545;
    border-radius: 4px;
    background-color: #fff8f8;
    margin: 10px 0;
    font-size: 0.9em;
    
    pre {
      margin-top: 8px;
      background-color: #f8f9fa;
      padding: 8px;
      border-radius: 4px;
      overflow-x: auto;
      font-size: 0.8em;
    }
  }
`;

// Helper function to extract mermaid content (removes code fence markers)
const extractMermaidContent = (content) => {
  if (!content) return '';
  
  // Handle both string and array content
  if (Array.isArray(content)) {
    return content.slice(1, content.length - 1).join('\n').trim();
  }
  
  const lines = content.split('\n');
  
  // Check if content has mermaid code fence markers
  if (lines.length >= 2 && lines[0].trim().startsWith('```mermaid') && lines[lines.length - 1].trim() === '```') {
    return lines.slice(1, lines.length - 1).join('\n').trim();
  }
  
  return content.trim();
};

// Helper function to create mermaid markdown
const createMermaidMarkdown = (mermaidContent) => {
  if (!mermaidContent) return '';
  
  // return [
  //   '```mermaid',
  //   mermaidContent,
  //   '```'
  // ].join('\n');
  return mermaidContent;
};

/**
 * MermaidBlock component for rendering and editing Mermaid diagrams
 * 
 * @param {Object} props Component props
 * @param {boolean} props.isActive Whether the block is active/being edited
 * @param {string|Array} props.content Mermaid block content (markdown format)
 * @param {Function} props.onClick Handler for click events
 * @param {Function} props.onContentChange Callback when content changes
 * @param {Function} props.commitContent Callback to commit content changes
 * @param {Object} props.customStyles Custom styles for the container
 */
const MermaidBlock = React.forwardRef(({
  isActive = false,
  content = '',
  onClick,
  onContentChange,
  commitContent,
  customStyles = {},
  ...otherProps
}, ref) => {
  const editorRef = useRef(null);
  const mermaidContainerRef = useRef(null);
  const renderTimeoutRef = useRef(null);
  
  const [initialContent, setInitialContent] = useState(
    Array.isArray(content) ? content.join('\n') : content
  );
  const [isEditing, setIsEditing] = useState(isActive);
  const [isRendered, setIsRendered] = useState(false);
  const [error, setError] = useState(null);
  const [renderedContent, setRenderedContent] = useState('');
  const prevIsActiveRef = useRef(isActive);
  
  // Extract the mermaid diagram content
  const mermaidContent = useMemo(() => {
    return extractMermaidContent(content);
  }, [content]);
  
  // Update content when it changes from parent
  useEffect(() => {
    const contentStr = Array.isArray(content) ? content.join('\n') : content;
    
    // Only update if not actively editing
    if (!isEditing) {
      setInitialContent(contentStr);
      setRenderedContent(extractMermaidContent(content));
    }
  }, [content, isEditing]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
        renderTimeoutRef.current = null;
      }
    };
  }, []);
  
  // Handle active state changes
  useEffect(() => {
    // Handle becoming active
    if (isActive && !prevIsActiveRef.current) {
      setIsEditing(true);
      setIsRendered(false);
      
      // Focus the editor when it becomes active
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focus();
          
          // Place cursor at end
          const selection = window.getSelection();
          const range = document.createRange();
          
          if (editorRef.current.lastChild) {
            range.setStartAfter(editorRef.current.lastChild);
          } else {
            range.setStart(editorRef.current, 0);
          }
          
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }, 10);
    }
    
    // Handle becoming inactive
    if (!isActive && prevIsActiveRef.current) {
      // Commit content when going inactive
      if (editorRef.current && commitContent) {
        const currentContent = editorRef.current.innerText;
        if (currentContent !== initialContent) {
          commitContent(currentContent);
          setInitialContent(currentContent);
        }
      }
      setIsEditing(false);
    }
    
    prevIsActiveRef.current = isActive;
  }, [isActive, initialContent, commitContent]);
  
  // Handle mermaid rendering
  useEffect(() => {
    // Skip rendering if in edit mode
    if (isEditing) return;
    
    // Reset rendering state
    setIsRendered(false);
    setError(null);
    
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current);
    }
    
    // Only render if we have content and a container
    if (!mermaidContent || !mermaidContainerRef.current) return;
    
    // Significant delay before rendering to avoid jitter during editing
    renderTimeoutRef.current = setTimeout(() => {
      try {
        // Initialize mermaid if needed
        if (typeof mermaid !== 'undefined' && !mermaid.initialized) {
          mermaid.initialize({
            startOnLoad: true,
            theme: 'default',
            securityLevel: 'loose',
          });
        }
        
        // Skip if content is empty
        if (!mermaidContent.trim()) {
          setError('Empty diagram content');
          return;
        }
        
        setRenderedContent(mermaidContent);
        
        // Use a callback to render after the component has updated
        setTimeout(() => {
          if (mermaidContainerRef.current) {
            try {
              // Find the mermaid div within our container and render
              const mermaidDiv = mermaidContainerRef.current.querySelector('.mermaid');
              if (mermaidDiv) {
                mermaid.contentLoaded();
                setIsRendered(true);
              }
            } catch (err) {
              console.error('Error during mermaid rendering:', err);
              setError(err.message);
            }
          }
        }, 10);
      } catch (error) {
        console.error('Error rendering mermaid diagram:', error);
        setError(error.message);
      }
    }, 300); // Delay to avoid rendering during editing
    
    return () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
        renderTimeoutRef.current = null;
      }
    };
  }, [mermaidContent, isEditing]);
  
  // Handle input changes
  const handleInput = useCallback(() => {
    if (editorRef.current && onContentChange) {
      const currentContent = editorRef.current.innerText;
      onContentChange(currentContent);
    }
  }, [onContentChange]);
  
  // Handle blur events
  const handleBlur = useCallback(() => {
    if (editorRef.current && commitContent) {
      const currentContent = editorRef.current.innerText;
      if (currentContent !== initialContent) {
        commitContent(currentContent);
        setInitialContent(currentContent);
      }
    }
    setIsEditing(false);
  }, [initialContent, commitContent]);
  
  // Handle key presses
  const handleKeyDown = useCallback((e) => {
    // Handle ESC - revert changes
    if (e.key === 'Escape' && editorRef.current && initialContent !== editorRef.current.innerText) {
      e.preventDefault();
      editorRef.current.innerText = initialContent;
      
      if (onContentChange) {
        onContentChange(initialContent);
      }
      
      setIsEditing(false);
      return;
    }
  }, [initialContent, onContentChange]);
  
  // Render the component
  return (
    <MermaidBlockContainer
      isActive={isActive}
      customStyles={customStyles}
      ref={ref}
      {...otherProps}
    >
      {isEditing ? (
        <MermaidEditorContainer
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          dangerouslySetInnerHTML={{ __html: initialContent }}
          onInput={handleInput}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <MermaidViewContainer onClick={onClick} ref={mermaidContainerRef}>
          {error ? (
            <div className="diagram-error">
              Error rendering diagram: {error}
              <pre>{mermaidContent}</pre>
            </div>
          ) : (
            <div className="mermaid-container">
              {!isRendered && (
                <div className="mermaid-placeholder">
                  Mermaid diagram
                </div>
              )}
              
              {renderedContent && (
                <div className="mermaid">
                  {renderedContent}
                </div>
              )}
            </div>
          )}
        </MermaidViewContainer>
      )}
    </MermaidBlockContainer>
  );
});

MermaidBlock.displayName = 'MermaidBlock';

export default MermaidBlock;
export { extractMermaidContent, createMermaidMarkdown };
